import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Lock, Eye, EyeOff, CheckCircle, Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { api } from "../App";
import { toast } from "sonner";

export default function ForcePasswordChangeModal({ isOpen, onPasswordChanged, userName }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Password strength check
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: "", color: "" };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return { level: 1, text: t('auth.weak') || 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { level: 2, text: t('auth.medium') || 'Medium', color: 'bg-yellow-500' };
    if (strength <= 4) return { level: 3, text: t('auth.strong') || 'Strong', color: 'bg-green-500' };
    return { level: 4, text: t('auth.veryStrong') || 'Very Strong', color: 'bg-emerald-600' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const validateForm = () => {
    if (!currentPassword) {
      setError(t('auth.enterCurrentPassword') || "Please enter your current password");
      return false;
    }
    if (!newPassword) {
      setError(t('auth.enterNewPassword') || "Please enter a new password");
      return false;
    }
    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength') || "Password must be at least 8 characters");
      return false;
    }
    if (newPassword === currentPassword) {
      setError(t('auth.passwordMustBeDifferent') || "New password must be different from current password");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch') || "Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success(t('auth.passwordChangedSuccess') || "Password changed successfully!");
      
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      // Notify parent
      if (onPasswordChanged) {
        onPasswordChanged();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('auth.passwordChangeError') || "Failed to change password";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md z-[100]" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="force-password-change-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <span>{t('auth.passwordChangeRequired') || 'Password Change Required'}</span>
          </DialogTitle>
          <DialogDescription className="text-left">
            {t('auth.passwordChangeRequiredDesc') || 'For security reasons, you must change your password before continuing.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Info */}
          <div className="bg-stone-50 rounded-lg p-3 text-sm">
            <span className="text-slate-500">{t('auth.loggedInAs') || 'Logged in as'}:</span>
            <span className="ml-2 font-medium text-slate-700">{userName}</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Current Password */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword">{t('auth.currentPassword') || 'Current Password'}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">{t('auth.newPassword') || 'New Password'}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded-full ${
                        level <= passwordStrength.level ? passwordStrength.color : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-xs ${
                  passwordStrength.level <= 1 ? 'text-red-600' : 
                  passwordStrength.level <= 2 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {t('auth.passwordStrength') || 'Password strength'}: {passwordStrength.text}
                </p>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('auth.confirmNewPassword') || 'Confirm New Password'}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 pr-10"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Match indicator */}
            {confirmPassword && (
              <p className={`text-xs flex items-center gap-1 ${
                newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'
              }`}>
                {newPassword === confirmPassword ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    {t('auth.passwordsMatch') || 'Passwords match'}
                  </>
                ) : (
                  t('auth.passwordsDoNotMatch') || 'Passwords do not match'
                )}
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            <p className="font-medium mb-1">{t('auth.passwordRequirements') || 'Password Requirements'}:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>{t('auth.reqMinLength') || 'At least 8 characters'}</li>
              <li>{t('auth.reqMixCase') || 'Mix of uppercase and lowercase letters'}</li>
              <li>{t('auth.reqNumbers') || 'At least one number'}</li>
              <li>{t('auth.reqSpecial') || 'At least one special character (!@#$%^&*)'}</li>
            </ul>
          </div>

          <DialogFooter>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.saving') || 'Saving...'}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  {t('auth.changePassword') || 'Change Password'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
